import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";

const carousel = [
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_ai-education-hub-46/artifacts/wlkao80z_ChatGPT%20Image%20Jul%2020%2C%202026%2C%2011_49_19%20AM%20%281%29.png",
    alt: "ProBodh Quest — AI Test Paper Generator and Assessment Dashboard",
  },
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_ai-education-hub-46/artifacts/sa16v3wj_ChatGPT%20Image%20Jul%2020%2C%202026%2C%2011_49_19%20AM%20%282%29.png",
    alt: "ProBodh DNA — AI Rubric Builder with board, department and teacher rules",
  },
  {
    src: "https://customer-assets-7cd3h4nn.emergentagent.net/job_ai-education-hub-46/artifacts/fjkxtniz_ChatGPT%20Image%20Jul%2020%2C%202026%2C%2011_49_20%20AM%20%283%29.png",
    alt: "ProBodh Focus — Pre-testing, Feynman Response Box and Metacognitive Reflection",
  },
];

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % carousel.length);
    }, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      className="relative bg-off overflow-hidden"
      data-testid="hero-section"
    >
      {/* soft gradient ambient */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 40% at 20% 10%, rgba(199,239,223,0.35) 0%, rgba(255,255,255,0) 60%), radial-gradient(50% 40% at 90% 20%, rgba(255,205,209,0.35) 0%, rgba(255,255,255,0) 60%)",
        }}
      />

      <div className="relative mx-auto max-w-[1200px] px-5 md:px-8 pt-14 md:pt-20 lg:pt-24 pb-16 md:pb-24">
        <div className="text-center max-w-[900px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="chip mx-auto"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-accent" />
            AI education platform · Built for India
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 md:mt-7 font-display text-[36px] leading-[1.02] sm:text-5xl md:text-6xl lg:text-[64px] text-ink"
            data-testid="hero-headline"
          >
            Traditional Values with a Modernised Approach to{" "}
            <span className="font-italic-serif text-accent">
              Teach, Learn and Grow.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 md:mt-7 max-w-[660px] mx-auto text-[15px] md:text-[17px] leading-relaxed text-ink-soft"
            data-testid="hero-subheadline"
          >
            We connect the dots between{" "}
            <em className="not-italic font-semibold text-ink">
              schools, teachers, students and parents
            </em>{" "}
            — one AI-native platform where curriculum, assessment, study and
            reporting finally live under one roof.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 md:mt-10 flex flex-wrap justify-center items-center gap-3"
          >
            <a
              href="#contact"
              className="btn-pill btn-primary"
              data-testid="hero-cta-contact"
            >
              Book a walk-through
              <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>

        {/* Product mockup — big card with soft accent frame */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-14 md:mt-20"
          data-testid="hero-mockup"
        >
          <div className="relative rounded-2xl md:rounded-[28px] overflow-hidden bg-mint p-3 md:p-6 card-shadow-lg">
            <div
              className="relative rounded-xl md:rounded-2xl overflow-hidden bg-white-card aspect-[16/10] sm:aspect-[16/9]"
              data-testid="hero-carousel"
              role="region"
              aria-label="ProBodh product screenshots carousel"
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={idx}
                  src={carousel[idx].src}
                  alt={carousel[idx].alt}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  initial={{ opacity: 0, x: 60 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -60 }}
                  transition={{
                    opacity: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
                    x: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                  }}
                  data-testid={`hero-carousel-slide-${idx}`}
                />
              </AnimatePresence>

              {/* Dot indicators */}
              <div
                className="absolute bottom-3 md:bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-ink/70 backdrop-blur-sm rounded-full px-3 py-2"
                data-testid="hero-carousel-dots"
              >
                {carousel.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIdx(i)}
                    aria-label={`Show slide ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      i === idx ? "w-6 bg-white-card" : "w-1.5 bg-white-card/50 hover:bg-white-card/80"
                    }`}
                    data-testid={`hero-carousel-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
